import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import LaneNav from "../components/LaneNav";
import { toast } from "sonner";
import { UploadSimple, Sparkle, CheckCircle, ArrowLeft, MagnifyingGlass, Link as LinkIcon, ArrowSquareOut } from "@phosphor-icons/react";
import { outboundRetailerUrl, trackBuyNowClick } from "../lib/retailerLinks";

export default function AddPurchase() {
  const [retailers, setRetailers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    product_name: "",
    product_url: "",
    retailer_id: "",
    category: "",
    purchase_price: "",
    purchase_date: new Date().toISOString().slice(0, 10),
  });
  const [busy, setBusy] = useState(false);
  const [ocrStage, setOcrStage] = useState("idle");
  const [ocrResult, setOcrResult] = useState(null);
  const [urlPreview, setUrlPreview] = useState(null);
  const [urlBusy, setUrlBusy] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [skipReminder, setSkipReminder] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lineItems, setLineItems] = useState([]);
  const [selectedLineIndex, setSelectedLineIndex] = useState(-1);
  const [priceMode, setPriceMode] = useState("incl"); // "incl" | "excl"
  const [autoMatch, setAutoMatch] = useState(null); // {url, name, price, confidence, retailer_name}
  const [autoMatchStage, setAutoMatchStage] = useState("idle"); // idle | searching | found | none
  const navigate = useNavigate();

  // Drag and drop handlers. We accept a single file at a time - the
  // backend OCR endpoint takes one upload, and multi-file receipt
  // attachment is a separate concern. preventDefault is required on
  // dragover otherwise the browser opens the file directly.
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleReceipt(file);
  };

  useEffect(() => {
    (async () => {
      const [r, c] = await Promise.all([api.get("/retailers"), api.get("/categories")]);
      setRetailers(r.data); setCategories(c.data);
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const previewUrl = async () => {
    if (!form.product_url) { toast.error("Paste a PB Tech product URL first"); return; }
    setUrlBusy(true);
    setUrlPreview(null);
    try {
      const { data } = await api.post("/scrape/preview", { url: form.product_url });
      setUrlPreview(data);
      setForm((f) => ({
        ...f,
        product_name: data.name || f.product_name,
        retailer_id: data.retailer_id || f.retailer_id,
        purchase_price: f.purchase_price || (data.price ? String(data.price) : ""),
        image_url: data.image || f.image_url,
      }));
      toast.success("Product found - fields autofilled");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("Unsupported retailer")) {
        toast.error("We don't support this retailer yet. Try PB Tech, JB Hi-Fi, or Harvey Norman.");
      } else if (detail.includes("403") || detail.toLowerCase().includes("blocked")) {
        toast.error("The retailer's page blocked our scraper. Fill the fields in manually below.");
      } else if (detail) {
        toast.error(`Couldn't read this URL: ${detail}`);
      } else {
        toast.error("Couldn't read this URL - try again, or fill in fields manually.");
      }
    } finally { setUrlBusy(false); }
  };

  // Look up a live product URL on the retailer's site for a freshly
  // parsed receipt item, so daily tracking can start without the user
  // pasting a URL. Best-effort: a null match just means "fall back to
  // the manual URL field". We hand the form's resolved retailer_id over
  // (not always the same as data.retailer_id, since the user may have
  // overridden it) so the matcher hits the right retailer.
  const runAutoMatch = async ({ productName, retailerId, sku }) => {
    if (!productName || !retailerId) {
      setAutoMatch(null);
      setAutoMatchStage("idle");
      return;
    }
    setAutoMatch(null);
    setAutoMatchStage("searching");
    try {
      const { data } = await api.post("/ocr/match-listing", {
        product_name: productName,
        retailer_id: retailerId,
        sku: sku || null,
      });
      if (data?.match?.url) {
        setAutoMatch(data.match);
        setAutoMatchStage("found");
        // Autofill the URL field with the matched listing and run a
        // proper preview-scrape so the on-sale + bonus-offer panels
        // light up the same way they do for a pasted URL.
        setForm((f) => ({ ...f, product_url: data.match.url }));
      } else {
        setAutoMatchStage("none");
      }
    } catch {
      setAutoMatchStage("none");
    }
  };

  const handleReceipt = async (file) => {
    if (!file) return;
    setReceiptFile(file);
    setOcrStage("uploading");
    const fd = new FormData();
    fd.append("file", file);
    try {
      setOcrStage("processing");
      const { data } = await api.post("/ocr/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOcrResult(data);
      setOcrStage("done");
      const items = Array.isArray(data.line_items) ? data.line_items : [];
      // Multi-item receipt: open the picker so the user chooses which
      // product to track. We don't autofill anything until they pick -
      // that way a 5-line receipt doesn't quietly land on whichever
      // happened to be first.
      if (items.length > 1) {
        setLineItems(items);
        // Pre-select the largest line by default (usually the headline
        // purchase the user actually wants to protect).
        const biggest = items.reduce((best, it) => (
          (it.line_total_incl_gst || 0) > (best?.line_total_incl_gst || 0) ? it : best
        ), null);
        setSelectedLineIndex(biggest ? items.indexOf(biggest) : 0);
        setForm((f) => ({
          ...f,
          retailer_id: data.retailer_id || f.retailer_id,
          purchase_date: data.purchase_date || f.purchase_date,
        }));
        toast.success(`Found ${items.length} items - pick the one to track`);
      } else {
        // Single-item path keeps the original autofill behaviour.
        setLineItems([]);
        setSelectedLineIndex(-1);
        setForm((f) => ({
          ...f,
          product_name: data.product_name || f.product_name,
          retailer_id: data.retailer_id || f.retailer_id,
          purchase_price: data.purchase_price ? String(data.purchase_price) : f.purchase_price,
          purchase_date: data.purchase_date || f.purchase_date,
        }));
        toast.success("Receipt scanned - fields autofilled and the file will be saved with this purchase");
        // Fire-and-forget auto-match for the single-item case so daily
        // tracking can start without the user pasting a URL.
        if (data.product_name && data.retailer_id) {
          runAutoMatch({ productName: data.product_name, retailerId: data.retailer_id });
        }
      }
    } catch (e) {
      setOcrStage("idle");
      toast.error(e?.response?.data?.detail || "Couldn't read the receipt - try uploading a clearer photo, or fill in the fields manually below.");
    }
  };

  // Pick a specific line item from a multi-item receipt to autofill the
  // form. Always autofills with GST-inclusive price (what the customer
  // actually paid) - the toggle in the form lets them swap if they want
  // to track the ex-GST figure for accounting reasons.
  const pickLineItem = (idx) => {
    const item = lineItems[idx];
    if (!item) return;
    setSelectedLineIndex(idx);
    setForm((f) => ({
      ...f,
      product_name: item.product_name || f.product_name,
      purchase_price: item.line_total_incl_gst != null
        ? String(item.line_total_incl_gst)
        : (item.unit_price_incl_gst != null ? String(item.unit_price_incl_gst) : f.purchase_price),
    }));
    // Try to find the matching live listing on the same retailer's site
    // so we can autofill the product URL and start daily tracking. Uses
    // the OCR result's retailer_id (already on the form) and the line
    // item's SKU when available for higher precision.
    const retailerId = ocrResult?.retailer_id || form.retailer_id;
    if (item.product_name && retailerId) {
      runAutoMatch({ productName: item.product_name, retailerId, sku: item.sku });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.retailer_id || !form.category) { toast.error("Retailer and category required"); return; }
    if (!receiptFile && !skipReminder) {
      // Soft reminder - one-time. If they hit Submit again without a file, we proceed.
      setSkipReminder(true);
      toast("Tip: attach the receipt now so you've got everything in one place if you ever need to claim a warranty.", { duration: 4500 });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, purchase_price: parseFloat(form.purchase_price) };
      const { data } = await api.post("/purchases", payload);
      // Best-effort: attach the receipt file if user uploaded one
      if (receiptFile) {
        try {
          const fd = new FormData();
          fd.append("file", receiptFile);
          await api.post(`/purchases/${data.purchase_id}/attach-receipt`, fd);
        } catch (err) {
          // Non-fatal - the purchase is already saved, the receipt just didn't attach
          toast.error("Purchase saved, but the receipt didn't attach. You can add it from the purchase page.");
        }
      }
      toast.success("Purchase added - tracking started");
      navigate(`/dashboard/purchase/${data.purchase_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add purchase");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
        <LaneNav active="purchases" />
        <button onClick={() => navigate("/dashboard")} className="text-sm font-semibold inline-flex items-center gap-1 text-[#52525b] hover:text-black mb-6" data-testid="back-to-dashboard">
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <div className="pg-overline mb-3">New tracking</div>
        <h1 className="font-heading text-4xl font-black tracking-tighter mb-10">Add a purchase<span className="text-[#002FA7]">.</span></h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Receipt upload + AI fill */}
          <div className="lg:col-span-5">
            <div className="pg-overline mb-3">Receipt - keep it on file</div>
            <label
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              className={`block bg-[#f7f7f8] border-2 border-dashed py-10 px-6 text-center cursor-pointer transition-colors ${
                dragActive
                  ? "border-[#002FA7] bg-[#002FA7]/10"
                  : ocrStage === "done"
                    ? "border-[#16A34A] bg-[#16A34A]/5"
                    : "border-[#a1a1aa] hover:border-[#002FA7] hover:bg-[#002FA7]/5"
              }`}
              data-testid="receipt-upload-area"
            >
              <input type="file" accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,.jpg,.jpeg,.png,.gif,.webp,.pdf" className="hidden" onChange={(e) => handleReceipt(e.target.files?.[0])} data-testid="receipt-file-input" />
              {ocrStage === "idle" && (<><UploadSimple size={28} className="mx-auto mb-3" /><div className="font-heading font-bold tracking-tight">{dragActive ? "Drop it here" : "Drop your receipt here, or click to choose"}</div><p className="text-[#52525b] text-sm mt-1">JPG, PNG or PDF. We'll autofill the form below and keep the file on hand for warranty claims later.</p></>)}
              {ocrStage === "uploading" && (<><Sparkle size={28} className="mx-auto mb-3 animate-pulse" /><div className="font-heading font-bold">Uploading…</div></>)}
              {ocrStage === "processing" && (<><Sparkle size={28} className="mx-auto mb-3 animate-pulse" /><div className="font-heading font-bold">Reading the receipt…</div><p className="text-[#52525b] text-sm mt-1">Extracting product, retailer & price.</p></>)}
              {ocrStage === "done" && (<><CheckCircle size={28} weight="fill" color="#16A34A" className="mx-auto mb-3" /><div className="font-heading font-bold">Receipt scanned</div><p className="text-[#52525b] text-sm mt-1">Fields autofilled. The receipt will be saved with this purchase when you submit.</p></>)}
            </label>
            {ocrResult && (
              <div className="mt-4 border border-[#e4e4e7] bg-white p-4 text-sm" data-testid="ocr-result">
                <div className="pg-overline mb-2">Extracted</div>
                <ul className="space-y-1 font-mono-pg text-xs text-[#52525b]">
                  <li>Product: <span className="text-black">{ocrResult.product_name || "-"}</span></li>
                  <li>Retailer: <span className="text-black">{ocrResult.retailer || "-"}</span></li>
                  <li>Price: <span className="text-black">{ocrResult.purchase_price ? `$${ocrResult.purchase_price}` : "-"}</span></li>
                  <li>Date: <span className="text-black">{ocrResult.purchase_date || "-"}</span></li>
                </ul>
              </div>
            )}

            {/* MULTI-ITEM PICKER - shown only when the receipt had more
                than one product line. User picks the item to track; the
                others remain visible so they can switch or re-add later. */}
            {lineItems.length > 1 && (
              <div className="mt-4 border border-[#002FA7] bg-white" data-testid="line-item-picker">
                <div className="bg-[#002FA7] text-white px-4 py-3">
                  <div className="font-bold text-sm">This receipt has {lineItems.length} items</div>
                  <div className="text-[11px] text-white/75 mt-0.5">Pick the one you want to track. You can add the others later.</div>
                </div>
                <div className="divide-y divide-[#e4e4e7]">
                  {lineItems.map((it, idx) => {
                    const isSelected = idx === selectedLineIndex;
                    const showInclPrice = priceMode === "incl";
                    const displayPrice = showInclPrice
                      ? it.line_total_incl_gst
                      : (it.unit_price_excl_gst != null && it.quantity
                          ? it.unit_price_excl_gst * it.quantity
                          : null);
                    return (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => pickLineItem(idx)}
                        className={`w-full px-4 py-3 flex items-start gap-3 text-left transition-colors ${
                          isSelected ? "bg-[#002FA7]/10" : "hover:bg-[#f7f7f8]"
                        }`}
                        data-testid={`line-item-${idx}`}
                      >
                        <div className={`w-4 h-4 mt-1 flex-shrink-0 border-2 ${isSelected ? "bg-[#002FA7] border-[#002FA7]" : "border-[#a1a1aa]"}`}>
                          {isSelected && <CheckCircle size={12} color="#fff" weight="fill" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-sm truncate">{it.product_name}</div>
                          <div className="text-[11px] text-[#52525b] mt-0.5 font-mono-pg">
                            {it.quantity > 1 ? `${it.quantity} × ` : ""}
                            {it.sku ? `SKU ${it.sku} · ` : ""}
                            {priceMode === "incl" ? "incl GST" : "excl GST"}
                          </div>
                        </div>
                        <div className="font-mono-pg text-sm font-bold flex-shrink-0">
                          {displayPrice != null ? `$${Number(displayPrice).toFixed(2)}` : "-"}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* GST toggle + freight + total summary */}
                <div className="bg-[#f7f7f8] border-t border-[#e4e4e7] px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono-pg uppercase tracking-widest">
                  <div className="inline-flex items-center gap-1 border border-[#e4e4e7] bg-white" data-testid="gst-toggle">
                    <button
                      type="button"
                      onClick={() => setPriceMode("incl")}
                      className={`px-3 py-1.5 font-bold ${priceMode === "incl" ? "bg-[#0a0a0a] text-white" : "text-[#52525b]"}`}
                      data-testid="gst-toggle-incl"
                    >
                      Incl GST
                    </button>
                    <button
                      type="button"
                      onClick={() => setPriceMode("excl")}
                      className={`px-3 py-1.5 font-bold ${priceMode === "excl" ? "bg-[#0a0a0a] text-white" : "text-[#52525b]"}`}
                      data-testid="gst-toggle-excl"
                    >
                      Excl GST
                    </button>
                  </div>
                  <div className="text-[#52525b]">
                    {ocrResult?.freight ? <span className="mr-3">Freight: <span className="text-[#0a0a0a]">${Number(ocrResult.freight).toFixed(2)}</span></span> : null}
                    {ocrResult?.total_incl_gst ? <span>Receipt total: <span className="text-[#0a0a0a]">${Number(ocrResult.total_incl_gst).toFixed(2)}</span></span> : null}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-7 space-y-5" data-testid="add-purchase-form">
            <Field label="Product name" required>
              <input type="text" required value={form.product_name} onChange={(e)=>update("product_name", e.target.value)} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm" placeholder="e.g. LG OLED C3 65 inch" data-testid="form-product-name" />
            </Field>
            <Field label="Product URL (optional)">
              <div className="flex gap-2">
                <input type="url" value={form.product_url} onChange={(e)=>update("product_url", e.target.value)} className="flex-1 border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm" placeholder="https://www.pbtech.co.nz/product/…" data-testid="form-product-url" />
                <button type="button" onClick={previewUrl} disabled={urlBusy || !form.product_url} className="border border-black font-bold px-4 inline-flex items-center gap-2 hover:bg-black hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="url-preview-btn">
                  <MagnifyingGlass size={16} /> {urlBusy ? "Reading…" : "Autofill"}
                </button>
              </div>

              {/* Auto-match status: shown only after a receipt is scanned
                  and we attempt to map the parsed product name back to a
                  live listing. Searching = spinner pill, found = green
                  pill with retailer link + confirm CTA, none = neutral
                  hint pill. Click "Confirm" wires the URL preview the
                  same way "Autofill" does for a pasted URL. */}
              {autoMatchStage === "searching" && (
                <div className="mt-3 inline-flex items-center gap-2 border border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] px-3 py-2 text-xs font-mono-pg uppercase tracking-widest" data-testid="auto-match-searching">
                  <Sparkle size={14} className="animate-pulse" />
                  Finding the live listing for this receipt item…
                </div>
              )}
              {autoMatchStage === "found" && autoMatch && (
                <div className="mt-3 border border-[#16A34A] bg-[#16A34A]/5 p-3" data-testid="auto-match-found">
                  <div className="flex items-start gap-3">
                    {autoMatch.image && (
                      <img src={autoMatch.image} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-12 h-12 object-cover border border-[#e4e4e7] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="pg-overline text-[#16A34A]">Match found · {autoMatch.retailer_name} · {Math.round((autoMatch.confidence || 0) * 100)}% confidence</div>
                      <div className="font-semibold text-sm truncate mt-0.5">{autoMatch.name}</div>
                      <div className="font-mono-pg text-xs text-[#52525b] mt-1">
                        {autoMatch.price ? `Listed at $${Number(autoMatch.price).toFixed(2)}` : "Live page found"}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={previewUrl} disabled={urlBusy} className="bg-[#16A34A] text-white font-bold px-3 py-1.5 text-xs hover:bg-[#138A3D] transition-colors disabled:opacity-50" data-testid="auto-match-confirm">
                          {urlBusy ? "Confirming…" : "Confirm match + start tracking"}
                        </button>
                        <a href={autoMatch.url} target="_blank" rel="noopener noreferrer" className="border border-[#0a0a0a] font-bold px-3 py-1.5 text-xs hover:bg-black hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="auto-match-open">
                          View on {autoMatch.retailer_name} <ArrowSquareOut size={12} weight="bold" />
                        </a>
                        <button type="button" onClick={() => { setAutoMatch(null); setAutoMatchStage("none"); setForm((f) => ({ ...f, product_url: "" })); }} className="text-xs text-[#52525b] hover:text-black underline" data-testid="auto-match-reject">
                          Not the right product
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {autoMatchStage === "none" && (
                <div className="mt-3 text-xs text-[#52525b] font-mono-pg" data-testid="auto-match-none">
                  No high-confidence live match - paste the product URL above and click Autofill if you want price tracking on this item.
                </div>
              )}
              {urlPreview && (
                <div className="mt-3 flex items-start gap-3 border border-[#16A34A] bg-[#16A34A]/5 p-3" data-testid="url-preview-result">
                  {urlPreview.image && <img src={urlPreview.image} alt="" referrerPolicy="no-referrer" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-14 h-14 object-cover border border-[#e4e4e7]" />}
                  <div className="flex-1 min-w-0">
                    <div className="pg-overline text-[#16A34A]">Live · {urlPreview.retailer_id === "pb_tech" ? "PB Tech" : urlPreview.retailer_id === "jb_hifi" ? "JB Hi-Fi" : urlPreview.retailer_id === "harvey_norman" ? "Harvey Norman" : "Retailer"}</div>
                    <div className="font-semibold text-sm truncate">{urlPreview.name}</div>
                    <div className="font-mono-pg text-xs text-[#52525b] mt-1">
                      Current: NZD ${Number(urlPreview.price).toFixed(2)}
                      {urlPreview.was_price && (
                        <span className="ml-2">· Was <span className="line-through">${Number(urlPreview.was_price).toFixed(2)}</span></span>
                      )}
                      <span className="ml-2">· {urlPreview.in_stock ? "In stock" : "Out of stock"}</span>
                    </div>
                  </div>
                </div>
              )}

              {urlPreview && urlPreview.on_sale && urlPreview.was_price && (
                <div className="mt-3 border border-[#D97706] bg-[#FFFBEB] p-4" data-testid="on-sale-warning">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 bg-[#D97706] text-white flex items-center justify-center font-bold text-sm">!</div>
                    <div className="flex-1 text-sm leading-relaxed">
                      <div className="font-heading font-bold text-[#0a0a0a]" data-testid="on-sale-warning-headline">
                        Heads up - this looks like part of {urlPreview.sale_label ? `a ${urlPreview.sale_label.replace(/ sales?$/i, " sale")}` : "a current sale"}.
                      </div>
                      <p className="text-[#0a0a0a] mt-1.5">
                        It's listed at <strong>${Number(urlPreview.price).toFixed(2)}</strong>, down from <strong className="line-through">${Number(urlPreview.was_price).toFixed(2)}</strong>. Price-protection watches for drops <strong>below today's price</strong>, so you may not see a further drop until the sale ends.
                      </p>
                      {urlPreview.sale_days_remaining !== null && urlPreview.sale_days_remaining !== undefined && (
                        <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-mono-pg uppercase tracking-widest bg-white border border-[#D97706]/30 px-2.5 py-1 text-[#D97706]" data-testid="on-sale-countdown">
                          {urlPreview.sale_days_remaining <= 0
                            ? "Sale likely ends today"
                            : urlPreview.sale_days_remaining === 1
                            ? "Sale likely ends tomorrow"
                            : `Sale likely runs ~${urlPreview.sale_days_remaining} more days`}
                          {urlPreview.sale_ends_on ? ` · through ${urlPreview.sale_ends_on}` : ""}
                        </p>
                      )}
                      <p className="text-[#52525b] mt-3 text-xs leading-relaxed">
                        Two better options: <strong>wait for the sale to end</strong> and add it then, or <button type="button" onClick={() => navigate("/dashboard/wishlist")} className="underline font-semibold text-[#002FA7] hover:text-[#00207A]" data-testid="on-sale-wishlist-link">add it to your wishlist instead</button> with today's price as the target.
                      </p>
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <a
                          href={outboundRetailerUrl(urlPreview.url, urlPreview.retailer_id)}
                          target="_blank"
                          rel="sponsored nofollow noopener noreferrer"
                          onClick={() => trackBuyNowClick({ retailerId: urlPreview.retailer_id, productUrl: urlPreview.url, context: "addpurchase_on_sale_warning" })}
                          className="inline-flex items-center gap-1.5 bg-[#0a0a0a] text-white font-bold px-4 py-2 text-sm hover:bg-[#002FA7] transition-colors"
                          data-testid="on-sale-buy-now"
                        >
                          Buy now at the sale price <ArrowSquareOut size={14} weight="bold" />
                        </a>
                        <span className="text-[10px] font-mono-pg text-[#52525b] uppercase tracking-widest">Opens the retailer's page in a new tab</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <p className="text-xs text-[#52525b] mt-2 font-mono-pg">Tip: a supported retailer URL unlocks real daily price tracking.</p>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Retailer" required>
                <select required value={form.retailer_id} onChange={(e)=>update("retailer_id", e.target.value)} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" data-testid="form-retailer">
                  <option value="">Select retailer…</option>
                  {retailers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Field>
              <Field label="Category" required>
                <select required value={form.category} onChange={(e)=>update("category", e.target.value)} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm bg-white" data-testid="form-category">
                  <option value="">Select category…</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Purchase price (NZD)" required>
                <input type="number" min="0" step="0.01" required value={form.purchase_price} onChange={(e)=>update("purchase_price", e.target.value)} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm" placeholder="3499.00" data-testid="form-price" />
              </Field>
              <Field label="Purchase date" required>
                <input type="date" required value={form.purchase_date} onChange={(e)=>update("purchase_date", e.target.value)} className="w-full border border-[#e4e4e7] py-3 px-3 outline-none focus:border-[#002FA7] text-sm" data-testid="form-date" />
              </Field>
            </div>
            <button type="submit" disabled={busy} className="bg-[#002FA7] text-white font-bold px-7 py-3 hover:bg-[#00207A] disabled:opacity-60" data-testid="form-submit">
              {busy ? "Starting tracking…" : (!receiptFile && skipReminder ? "Continue without receipt" : "Start tracking this purchase")}
            </button>
            {!receiptFile && skipReminder && (
              <p className="text-xs text-[#D97706] mt-2 font-mono-pg" data-testid="receipt-skip-warning">
                You can add the receipt later from the purchase page - but it's much easier while you've got it handy.
              </p>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="pg-overline">{label}{required && <span className="text-[#FF3B30]">*</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
